import React, { useCallback, useState } from "react";

import { useDispatch } from "Hooks/useDispatch";

import useSharedLocalStorage from "Hooks/useSharedLocalStorage";
import {
  Button,
  Checkbox,
  IconButton,
  Modal,
  Tooltip,
} from "@toddle-edu/ds-web";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import { UserBlockedOutlined, UserTickOutlined } from "@toddle-edu/ds-icons";
import useI18n from "Hooks/UseI18n";
import { updateViewOnlyUsers } from "MessagingV2/modules/MessagingModule";

import classes from "./BlockUser.scss";
import { isIdEqual } from "Utils";
import { getUserId } from "modules/CommonSelectors";

const LOCAL_STORAGE_KEY = "showMessagingV2blockUserModal";

const styles = {
  modalBody: {
    padding: "0px",
    paddingInline: "24px",
  },
};

const BlockUserModal = props => {
  const { userId, onClose, name, userType } = props;
  const { t } = useI18n(["common"]);
  const [showModalAgain, setShowModalAgain] = useSharedLocalStorage(
    LOCAL_STORAGE_KEY,
    false
  );

  const { chatId } = useChatOptions();
  const dispatch = useDispatch();

  const onBlock = () => {
    dispatch(
      updateViewOnlyUsers({ chatId, isBlocking: true, userId, name, userType })
    );
    onClose();
  };

  const toggleShowModalAgain = () => {
    setShowModalAgain(showModalAgain => !showModalAgain);
  };

  return (
    <Modal width="376px" height="max-content" onClose={onClose} dsVersion="2.0">
      <Modal.Header title={`${t("common:block_user_using_the_channel")}?`} />
      <Modal.Body style={styles.modalBody}>
        <div className={classes.modalBody}>
          <div className="text-body">
            {t("common:block_name_from_channel_info", {
              name,
            })}
          </div>
          <Checkbox
            dsVersion="2.0"
            onChange={toggleShowModalAgain}
            isChecked={showModalAgain}
          >
            <span className={classes.textSecondary}>
              {t("common:do_not_show_this_again")}
            </span>
          </Checkbox>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.modalFooter}>
          <Button
            dsVersion="2.0"
            variant="neutral"
            size="medium"
            type="outlined"
            onClick={onClose}
          >
            {t("common:cancel")}
          </Button>
          <Button
            dsVersion="2.0"
            variant="primary"
            size="medium"
            type="fill"
            onClick={onBlock}
          >
            {t("common:block_capital")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export const BlockUserButton = props => {
  const {
    userId,
    userType,
    isBlocked,
    name,
    size = "medium",
    dsVersion = "2.0",
    toolTipPlacement = "bottom",
    ...rest
  } = props;

  const [hideBlockModal] = useSharedLocalStorage(LOCAL_STORAGE_KEY, false);
  const dispatch = useDispatch();

  const [showBlockModal, setShowBlockModal] = useState(false);
  const { chatId } = useChatOptions();
  const { t } = useI18n(["common"]);

  const handleBlockUnblockClick = () => {
    if (!hideBlockModal && !isBlocked) {
      setShowBlockModal(true);
      return;
    }
    dispatch(
      updateViewOnlyUsers({
        chatId,
        isBlocking: !isBlocked,
        userId,
        name,
        userType,
      })
    );
  };

  const onCloseBlockModal = useCallback(() => {
    setShowBlockModal(false);
  }, []);

  const tooltipText = isBlocked
    ? t("common:unblock_user_from_channel")
    : t("common:block_from_channel");

  if (isIdEqual(userId, getUserId())) return null;

  return (
    <>
      <Tooltip tooltip={tooltipText} placement={toolTipPlacement} showArrow>
        <IconButton
          dsVersion={dsVersion}
          icon={isBlocked ? <UserTickOutlined /> : <UserBlockedOutlined />}
          type="plain"
          variant="neutral"
          size={size}
          {...rest}
          onClick={handleBlockUnblockClick}
        />
      </Tooltip>
      {showBlockModal ? (
        <BlockUserModal
          onClose={onCloseBlockModal}
          userId={userId}
          name={name}
          userType={userType}
        />
      ) : null}
    </>
  );
};
